import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';

import type { Board, IssueCard } from '@tasks/contracts';

import { transitionIssue } from '../../api/issues';
import { queryKeys } from '../../api/queryKeys';
import { useToast } from '../../components/Toast';
import { useFloatingStore } from '../../stores/floatingStore';
import { humanizeError } from '../../lib/errors';
import { moveCardInBoard } from './boardModel';

export type BoardDndContext = {
  readonly activeCard: IssueCard | null;
  readonly sensors: ReturnType<typeof useSensors>;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleDragCancel: () => void;
  readonly isTransitionAllowed: (card: IssueCard, toStatusId: string) => boolean;
};

/**
 * Drag-and-drop доски: оптимистичное перемещение с откатом при ошибке
 * (UC-BRD-02). Смена статуса идёт только через `/transitions`.
 */
export function useBoardDnd(
  projectKey: string,
  board: Board | null,
  allowedTransitions: ReadonlyMap<string, ReadonlySet<string>>,
): BoardDndContext {
  const [activeCard, setActiveCard] = useState<IssueCard | null>(null);
  const queryClient = useQueryClient();
  const toast = useToast();
  const beginDrag = useFloatingStore((state) => state.beginDrag);
  const endDrag = useFloatingStore((state) => state.endDrag);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const isTransitionAllowed = useCallback(
    (card: IssueCard, toStatusId: string): boolean => {
      if (card.statusId === toStatusId) {
        return true;
      }
      const allowed = allowedTransitions.get(card.statusId);
      return allowed === undefined ? true : allowed.has(toStatusId);
    },
    [allowedTransitions],
  );

  const mutation = useMutation({
    mutationFn: (variables: { readonly key: string; readonly toStatusId: string }) =>
      transitionIssue(variables.key, { toStatusId: variables.toStatusId, resolution: null }),
    onError: (cause: unknown, variables) => {
      // Откат: возвращаем предыдущее состояние доски с сервера.
      void queryClient.invalidateQueries({ queryKey: queryKeys.board.root(projectKey) });
      const human = humanizeError(cause);
      toast.error(
        human.code === 'TRANSITION_NOT_ALLOWED'
          ? 'Переход недоступен для этого статуса'
          : human.message,
        { code: human.code },
      );
      void variables;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.board.root(projectKey) });
    },
  });

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const card = event.active.data.current?.['card'] as IssueCard | undefined;
      if (card) {
        setActiveCard(card);
        beginDrag();
      }
    },
    [beginDrag],
  );

  const handleDragCancel = useCallback(() => {
    setActiveCard(null);
    endDrag();
  }, [endDrag]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveCard(null);
      endDrag();
      const { active, over } = event;
      if (!over || !board) {
        return;
      }
      const card = active.data.current?.['card'] as IssueCard | undefined;
      const toStatusId = over.data.current?.['statusId'] as string | undefined;
      if (!card || !toStatusId || card.statusId === toStatusId) {
        return;
      }

      if (!isTransitionAllowed(card, toStatusId)) {
        toast.error('Переход недоступен', {
          code: 'TRANSITION_NOT_ALLOWED',
          action: { label: 'Понятно', onClick: () => undefined },
        });
        return;
      }

      // Оптимистично: перемещаем карточку сразу.
      queryClient.setQueriesData<Board>({ queryKey: queryKeys.board.root(projectKey) }, (previous) =>
        previous ? moveCardInBoard(previous, card.id, toStatusId) : previous,
      );

      mutation.mutate({ key: card.key, toStatusId });
    },
    [board, endDrag, isTransitionAllowed, mutation, projectKey, queryClient, toast],
  );

  return { activeCard, sensors, handleDragStart, handleDragEnd, handleDragCancel, isTransitionAllowed };
}
